import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";

type RenderItemArgs = {
  selected: boolean;
};

export interface ComboboxProps<TItem> {
  items: TItem[];
  value?: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  triggerClassName?: string;
  contentClassName?: string;
  listClassName?: string;
  showClear?: boolean;
  clearLabel?: string;
  itemToValue: (item: TItem) => string;
  itemToLabel: (item: TItem) => string;
  itemToKeywords?: (item: TItem) => string[];
  renderItem?: (item: TItem, args: RenderItemArgs) => ReactNode;
  filterFn?: (value: string, search: string, keywords?: string[]) => number;
}

export function Combobox<TItem>({
  items,
  value,
  onValueChange,
  disabled,
  placeholder = "Select option",
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
  triggerClassName,
  contentClassName,
  listClassName,
  showClear = false,
  clearLabel = "Clear selection",
  itemToValue,
  itemToLabel,
  itemToKeywords,
  renderItem,
  filterFn,
}: ComboboxProps<TItem>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const selectedItem = items.find((item) => itemToValue(item) === value);
    return selectedItem ? itemToLabel(selectedItem) : "";
  }, [items, itemToLabel, itemToValue, value]);

  const hasSelection = Boolean(value && selectedLabel);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      const itemValue = itemToValue(item);
      const keywords = itemToKeywords
        ? itemToKeywords(item).filter((keyword): keyword is string => Boolean(keyword))
        : undefined;
      const score = filterFn ? filterFn(itemValue, search, keywords) : (() => {
        const haystack = `${itemValue} ${(keywords || []).join(" ")}`.toLowerCase();
        if (!needle) return 1;
        return haystack.includes(needle) ? 1 : 0;
      })();
      return score > 0;
    });
  }, [filterFn, itemToKeywords, itemToValue, items, search]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    globalThis.addEventListener("mousedown", onPointerDown);
    globalThis.addEventListener("keydown", onEscape);
    return () => {
      globalThis.removeEventListener("mousedown", onPointerDown);
      globalThis.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    triggerRef.current?.scrollIntoView({ block: "start", inline: "nearest" });
    setHighlightedIndex(0);
    const timeout = globalThis.setTimeout(() => inputRef.current?.focus(), 0);
    return () => globalThis.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  const visibleOptions = useMemo(() => {
    const options: Array<{ kind: "clear" } | { kind: "item"; item: TItem }> = [];
    if (showClear && hasSelection) {
      options.push({ kind: "clear" });
    }
    filteredItems.forEach((item) => options.push({ kind: "item", item }));
    return options;
  }, [filteredItems, hasSelection, showClear]);

  useEffect(() => {
    if (visibleOptions.length === 0) {
      setHighlightedIndex(0);
      return;
    }
    setHighlightedIndex((prev) => Math.min(prev, visibleOptions.length - 1));
  }, [visibleOptions]);

  const selectValue = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
    setSearch("");
    triggerRef.current?.focus();
  };

  const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!visibleOptions.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, visibleOptions.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const option = visibleOptions[highlightedIndex];
      if (!option) return;
      if (option.kind === "clear") {
        selectValue("");
      } else {
        selectValue(itemToValue(option.item));
      }
    }
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <Button
        ref={triggerRef}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        className={cn("w-full justify-between text-sm", triggerClassName)}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
          if (!open) {
            setSearch("");
          }
        }}
      >
        <span className="truncate">{selectedLabel || placeholder}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div
          className={cn(
            "absolute left-0 top-full z-[70] mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none",
            contentClassName,
          )}
        >
          <div className="flex items-center border-b px-3">
            <input
              ref={inputRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={searchPlaceholder}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div className={cn("max-h-[280px] overflow-y-auto p-1", listClassName)}>
            {visibleOptions.length === 0
              ? <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
              : visibleOptions.map((option, index) => {
                if (option.kind === "clear") {
                  const active = highlightedIndex === index;
                  return (
                    <button
                      key="__clear__"
                      type="button"
                      className={cn(
                        "relative flex w-full select-none items-center rounded-sm px-2 py-2 text-sm text-left outline-none transition-colors",
                        active
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent hover:text-accent-foreground",
                      )}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectValue("")}
                    >
                      <X className="mr-2 h-4 w-4" />
                      <span>{clearLabel}</span>
                    </button>
                  );
                }

                const item = option.item;
                const itemValue = itemToValue(item);
                const selected = itemValue === value;
                const active = highlightedIndex === index;

                return (
                  <button
                    key={itemValue}
                    type="button"
                    className={cn(
                      "relative flex w-full select-none items-center rounded-sm px-2 py-2 text-sm text-left outline-none transition-colors",
                      active
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent hover:text-accent-foreground",
                    )}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => selectValue(itemValue)}
                  >
                    <div className="flex w-full items-center gap-2">
                      <Check className={cn("h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
                      <div className="min-w-0 flex-1">
                        {renderItem
                          ? renderItem(item, { selected })
                          : <span className="truncate text-sm">{itemToLabel(item)}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
